// src/pages/ordens-servico/KioskNovaOS.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";

import { db } from "../../config/firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  runTransaction,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import "./KioskNovaOS.css";

async function obterProximoCodigoSequencialOs(grupoId) {
  const contadorRef = doc(db, "contadores", `os_${grupoId}`);

  const proximo = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(contadorRef);

    let atual = 0;
    if (snap.exists() && snap.data().ultimoCodigoSequencial != null) {
      atual = snap.data().ultimoCodigoSequencial;
    }

    const novoValor = atual + 1;

    transaction.set(
      contadorRef,
      { ultimoCodigoSequencial: novoValor, grupoId },
      { merge: true }
    );

    return novoValor;
  });

  return proximo;
}

function KioskNovaOS() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { empresaAtual } = useEmpresa();

  // Wizard steps (tablet)
  const [step, setStep] = useState(1);

  // Kiosk (tablet): mantém o app "preso" na tela, mas permite o "Voltar" do Android
  // para voltar o *wizard* (ex.: Step 2 -> Step 1). Assim, o botão voltar funciona
  // sem deixar o usuário sair da tela por acidente.
  const stepRef = useRef(1);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    const lock = () => {
      try {
        window.history.pushState({ kioskLock: true }, "", window.location.href);
      } catch (_) {}
    };

    lock();
    const onPopState = () => {
      // ✅ Se estiver no passo 2, o botão voltar do tablet retorna ao passo 1
      if (stepRef.current > 1) {
        setStep(1);
      }
      // E mantém o lock para não sair do Kiosk
      lock();
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logoUrl = empresaAtual?.logoUrl || null;
  const empresaFixaId = empresaAtual?.id || null;
  const empresaFixaNome = empresaAtual?.nome || "";

  const [logoFormat, setLogoFormat] = useState("wide");

  useEffect(() => {
    if (!logoUrl) {
      setLogoFormat("wide");
      return;
    }
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      if (ratio >= 0.8 && ratio <= 1.2) {
        setLogoFormat("square");
      } else {
        setLogoFormat("wide");
      }
    };
    img.onerror = () => setLogoFormat("wide");
    img.src = logoUrl;
  }, [logoUrl]);

  const [setores, setSetores] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [statusList, setStatusList] = useState([]);

  const [carregandoSetores, setCarregandoSetores] = useState(false);
  const [carregandoMaquinas, setCarregandoMaquinas] = useState(false);
  const [carregandoStatus, setCarregandoStatus] = useState(false);

  const [setorId, setSetorId] = useState("");
  const [maquinaId, setMaquinaId] = useState("");

  const [statusId, setStatusId] = useState("");

  const [defeitos, setDefeitos] = useState([]);
  const [carregandoDefeitos, setCarregandoDefeitos] = useState(false);
  const [defeitoId, setDefeitoId] = useState("");
  const [defeitoCustom, setDefeitoCustom] = useState("");
  const [descricao, setDescricao] = useState("");
  const [criticidade, setCriticidade] = useState("Média");
  const [maquinaParou, setMaquinaParou] = useState("Sim");
  const [tipoServico, setTipoServico] = useState("Corretiva");

  // usado no cabeçalho (topbar) para manter o mesmo layout entre as páginas do wizard
  const paginaAtualLabel = step === 1 ? "Localização" : "Problema";

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    async function carregarBase() {
      if (!usuarioLogado?.grupoId) return;

      const grupoId = usuarioLogado.grupoId;

      setCarregandoSetores(true);
      try {
        const setoresRef = collection(db, "setores");
        const qSet = query(setoresRef, where("grupoId", "==", grupoId));
        const snapSet = await getDocs(qSet);
        const listaSet = snapSet.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setSetores(listaSet);
      } catch (e) {
        console.error("Erro ao carregar setores:", e);
      } finally {
        setCarregandoSetores(false);
      }

      setCarregandoMaquinas(true);
      try {
        const maqRef = collection(db, "maquinas");
        const qMaq = query(maqRef, where("grupoId", "==", grupoId));
        const snapMaq = await getDocs(qMaq);
        const listaMaq = snapMaq.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setMaquinas(listaMaq);
      } catch (e) {
        console.error("Erro ao carregar máquinas:", e);
      } finally {
        setCarregandoMaquinas(false);
      }

      setCarregandoStatus(true);
      try {
        const statusRef = collection(db, "statusOs");
        const qStatus = query(statusRef, where("grupoId", "==", grupoId));
        const snapStatus = await getDocs(qStatus);
        const listaStatusDb = snapStatus.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const statusFabrica = [
          {id:"fab_aberta", nome:"Aberta", cor:"#2563eb", padrao:true},
          {id:"fab_aberta_24h", nome:"Aberta 24H", cor:"#7c3aed", padrao:true},
          {id:"fab_em_atendimento", nome:"Em Atendimento", cor:"#0ea5e9", padrao:true},
          {id:"fab_finalizada", nome:"Finalizada", cor:"#16a34a", padrao:true},
          {id:"fab_cancelada", nome:"Cancelada", cor:"#dc2626", padrao:true},
          {id:"fab_aguard_mat", nome:"Aguardando Material", cor:"#ca8a04", padrao:true},
          {id:"fab_solic_mat", nome:"Solicitação de Material", cor:"#d97706", padrao:true},
          {id:"fab_aguard_parada", nome:"Aguardando Parada de Máquina", cor:"#64748b", padrao:true}
        ];

        const listaStatus = [...statusFabrica, ...listaStatusDb];
        setStatusList(listaStatus);

        const padraoAberta24h =
          listaStatus.find((s) => s.id === "fab_aberta_24h") ||
          listaStatus.find((s) =>
            String(s.nome || "").toLowerCase().includes("aberta 24h")
          );
        const padraoAberta =
          listaStatus.find((s) => s.id === "fab_aberta") ||
          listaStatus.find(
            (s) => String(s.nome || "").toLowerCase() === "aberta"
          );

        if (padraoAberta24h) {
          setStatusId(padraoAberta24h.id);
        } else if (padraoAberta) {
          setStatusId(padraoAberta.id);
        } else if (listaStatus.length > 0) {
          setStatusId(listaStatus[0].id);
        }
      } catch (e) {
        console.error("Erro ao carregar status:", e);
      } finally {
        setCarregandoStatus(false);
      }
    }

    carregarBase();
  }, [usuarioLogado]);

  useEffect(() => {
    async function carregarDefeitosDaMaquina() {
      setDefeitos([]);
      setDefeitoId("");
      if (!usuarioLogado?.grupoId || !maquinaId) return;

      try {
        setCarregandoDefeitos(true);
        const defeitosRef = collection(db, "defeitos");
        const qDef = query(
          defeitosRef,
          where("grupoId", "==", usuarioLogado.grupoId),
          where("maquinasIds", "array-contains", maquinaId)
        );
        const snapDef = await getDocs(qDef);
        const listaDef = snapDef.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setDefeitos(listaDef);
      } catch (e) {
        console.error("Erro ao carregar defeitos da máquina:", e);
      } finally {
        setCarregandoDefeitos(false);
      }
    }

    carregarDefeitosDaMaquina();
  }, [usuarioLogado, maquinaId]);

  const setoresFiltrados = useMemo(() => {
    if (!empresaFixaId) return setores;
    return setores.filter((s) => s.empresaId === empresaFixaId);
  }, [setores, empresaFixaId]);

  const maquinasFiltradas = useMemo(() => {
    const base = !setorId ? maquinas : maquinas.filter((m) => m.setorId === setorId);
    return [...base].sort((a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" })
    );
  }, [maquinas, setorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro("Usuário sem grupo definido.");
      return;
    }

    if (!empresaFixaId) {
      setErro("Empresa não configurada para este Kiosk.");
      return;
    }
    if (!setorId) {
      setErro("Selecione o setor.");
      return;
    }
    if (!maquinaId) {
      setErro("Selecione a máquina.");
      return;
    }

    // Wizard: primeiro passo só coleta localização.
    if (step === 1) {
      setStep(2);
      return;
    }

    if (!defeitoId) {
      setErro("Selecione o defeito.");
      return;
    }

    if (defeitoId === "__OUTROS__" && !defeitoCustom.trim()) {
      setErro("Descreva o defeito.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || usuarioLogado.grupo || null;

    const setorSelecionado = setores.find((s) => s.id === setorId);
    const setorNome = setorSelecionado?.nome || "(sem nome)";

    const maquinaSelecionada = maquinas.find((m) => m.id === maquinaId);
    const maquinaNome = maquinaSelecionada?.nome || "(sem nome)";
    const maquinaChave = !!maquinaSelecionada?.maquinaChave;


    let statusInicial = statusList.find((s) => s.id === statusId);

    if (!statusInicial) {
      statusInicial =
        statusList.find((s) => s.id === "fab_aberta_24h") ||
        statusList.find((s) =>
          String(s.nome || "").toLowerCase().includes("aberta 24h")
        ) ||
        statusList.find((s) => s.id === "fab_aberta") ||
        statusList.find(
          (s) => String(s.nome || "").toLowerCase() === "aberta"
        ) ||
        statusList[0];
    }

    if (!statusInicial) {
      setErro("Não foi possível definir o status inicial.");
      return;
    }

    const statusIdInicial = statusInicial.id;
    const statusNomeInicial = statusInicial.nome || null;
    const statusCorInicial = statusInicial.cor || null;

    const isDefeitoCustom = defeitoId === "__OUTROS__";
    const defeitoSelecionado = isDefeitoCustom ? null : defeitos.find((d) => d.id === defeitoId) || null;
    const tituloOs = isDefeitoCustom ? defeitoCustom.trim() : (defeitoSelecionado?.nome || "Defeito não especificado");

    setSalvando(true);

    try {
      const sequencial = await obterProximoCodigoSequencialOs(grupoId);
      const codigo = `OS${String(sequencial).padStart(4, "0")}`;

      const novaRef = doc(collection(db, "ordensServico"));
      const parouSim = String(maquinaParou || "Sim").toLowerCase().trim() === "sim";
      await setDoc(novaRef, {
        codigo,
        codigoSequencial: sequencial,

        titulo: tituloOs,
        defeitoId: isDefeitoCustom ? null : (defeitoSelecionado ? defeitoSelecionado.id : defeitoId || null),
        defeitoNome: tituloOs,
        defeitoCustom: isDefeitoCustom,
        descricao: descricao.trim() || null,
        criticidade: criticidade || "Média",
        maquinaParou: maquinaParou || "Sim",
        // ✅ Controle de parada (estado atual) — não depende do status da OS
        maquinaParadaAtiva: parouSim,
        maquinaParouEm: parouSim ? serverTimestamp() : null,
        maquinaVoltouRodarEm: null,
        tipoServico: tipoServico || "Corretiva",

        grupoId,
        grupoNome: grupoNome || null,
        empresaId: empresaFixaId,
        empresaNome: empresaFixaNome,
        setorId,
        setorNome,
        maquinaId,
        maquinaNome,
        maquinaChave,

        statusId: statusIdInicial,
        statusNome: statusNomeInicial,
        statusCor: statusCorInicial,

        criadoPor: usuarioLogado?.uid || null,
        criadoPorNome: usuarioLogado?.nome || null,
        criadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSucesso(`OS ${codigo} criada com sucesso!`);
      setDefeitoId("");
      setDefeitoCustom("");
      setDefeitos([]);
      setDescricao("");
      setCriticidade("Média");
      setMaquinaParou("Sim");
      setTipoServico("Corretiva");
      setSetorId("");
      setMaquinaId("");
      setStep(1);
      setStep(1);
    } catch (err) {
      console.error(err);
      const code = err?.code || "";
      if (code === "permission-denied") {
        setErro("Sem permissão para abrir OS.");
      } else {
        setErro("Erro ao abrir OS. Tente novamente.");
      }
    } finally {
      setSalvando(false);
    }
  };

  const limparFormulario = () => {
    setSucesso("");
    setErro("");
    setStep(1);
  };

  return (
    <div className={`kiosk-root ${tema}`} data-testid="kiosk-nova-os">
      <div className="kiosk-bg-effects">
        <div className="kiosk-orb kiosk-orb-1"></div>
        <div className="kiosk-orb kiosk-orb-2"></div>
        <div className="kiosk-orb kiosk-orb-3"></div>
        <div className="kiosk-grid-pattern"></div>
      </div>

      <div className="kiosk-container">
        <aside className="kiosk-hero">
          <div className="kiosk-hero-content">
            <div className="kiosk-hero-inner">
              <div className="kiosk-brand-row">
              <div className="kiosk-logo-area">
                {logoUrl ? (
                  <div className={`kiosk-logo-wrapper kiosk-logo-${logoFormat}`}>
                    <img
                      src={logoUrl}
                      alt="Logo da empresa"
                      className="kiosk-logo-img"
                      data-testid="img-logo-empresa"
                    />
                  </div>
                ) : (
                  <div className="kiosk-logo-placeholder">
                    <div className="kiosk-logo-formats">
                      <div className="kiosk-format-option">
                        <div className="kiosk-format-preview kiosk-format-square">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                          </svg>
                        </div>
                        <span>Quadrado</span>
                        <span className="kiosk-format-size">200 x 200 px</span>
                      </div>
                      <div className="kiosk-format-divider">ou</div>
                      <div className="kiosk-format-option">
                        <div className="kiosk-format-preview kiosk-format-wide">
                          <svg width="48" height="24" viewBox="0 0 48 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="2" y="2" width="44" height="20" rx="2" />
                          </svg>
                        </div>
                        <span>Retangular</span>
                        <span className="kiosk-format-size">400 x 160 px</span>
                      </div>
                    </div>
                    <span className="kiosk-logo-hint">PNG transparente recomendado</span>
                  </div>
                )}
              </div>

              <div className="kiosk-brand-text">
                <div className="kiosk-brand-title kiosk-brand-title--stacked">
                  <span>Abrir Ordem</span>
                  <span>de Serviço</span>
                </div>
                <div className="kiosk-brand-subtitle kiosk-brand-subtitle--page">
                  Página: {paginaAtualLabel}
                </div>
              </div>
            </div>

            <div className="kiosk-hero-decoration">
              <div className="kiosk-hero-line"></div>
              <div className="kiosk-hero-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
                <span>Manutenção</span>
              </div>
            </div>
            </div>
          </div>
        </aside>

        <main className="kiosk-main">
          <div className="kiosk-main-inner">
            <header className="kiosk-topbar" aria-label="Cabeçalho do kiosk">
              <div className="kiosk-brand-row kiosk-brand-row--topbar">
                <div className="kiosk-logo-area">
                  {logoUrl ? (
                    <div className={`kiosk-logo-wrapper kiosk-logo-${logoFormat}`}>
                      <img
                        src={logoUrl}
                        alt="Logo da empresa"
                        className="kiosk-logo-img"
                        data-testid="img-logo-empresa-top"
                      />
                    </div>
                  ) : (
                    <div className="kiosk-logo-placeholder kiosk-logo-placeholder--compact">
                      <span className="kiosk-logo-hint">Clique para adicionar logo</span>
                    </div>
                  )}
                </div>

                <div className="kiosk-brand-text">
                  <div className="kiosk-brand-title kiosk-brand-title--stacked">
                    <span>Abrir Ordem</span>
                    <span>de Serviço</span>
                  </div>
                  <div className="kiosk-brand-subtitle kiosk-brand-subtitle--page">
                    Página: {paginaAtualLabel}
                  </div>
                </div>
              </div>
            </header>

            <div className="kiosk-form-card">
            {!sucesso && (
              <div className="kiosk-stepper" data-testid="kiosk-stepper">
                <button
                  type="button"
                  className={"kiosk-step " + (step === 1 ? "active" : "")}
                  onClick={() => setStep(1)}
                  aria-current={step === 1 ? "step" : undefined}
                >
                  <span className="kiosk-step-num">1</span>
                  <span className="kiosk-step-label">Localização</span>
                </button>
                <div className="kiosk-step-line" />
                <button
                  type="button"
                  className={"kiosk-step " + (step === 2 ? "active" : "")}
                  onClick={() => {
                    // só permite avançar se já escolheu setor + máquina
                    if (setorId && maquinaId) setStep(2);
                  }}
                  aria-current={step === 2 ? "step" : undefined}
                >
                  <span className="kiosk-step-num">2</span>
                  <span className="kiosk-step-label">Problema</span>
                </button>
              </div>
            )}

            {erro && (
              <div className="kiosk-alert kiosk-alert-error" data-testid="alert-error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M15 9l-6 6M9 9l6 6" />
                </svg>
                <span>{erro}</span>
              </div>
            )}

            {sucesso ? (
              <div className="kiosk-success-state">
                <div className="kiosk-success-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h2>{sucesso}</h2>
                <p>A equipe de manutenção foi notificada</p>
                <button
                  type="button"
                  className="kiosk-new-btn"
                  onClick={limparFormulario}
                  data-testid="button-nova-os"
                >
                  Abrir Nova OS
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="kiosk-form">
                <div className="kiosk-step-content">
                  {step === 1 ? (
                    <>
<div className="kiosk-form-section">
                  <h3 className="kiosk-section-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    </svg>
                    Localização
                  </h3>

                  <label className="kiosk-field">
                    <span className="kiosk-label">Tipo de Serviço</span>
                    <select
                      value={tipoServico}
                      onChange={(e) => setTipoServico(e.target.value)}
                      data-testid="select-tipo-servico"
                    >
                      <option value="Corretiva">Corretiva</option>
                      <option value="Melhoria">Melhoria</option>
                      <option value="Instalação">Instalação</option>
                    </select>
                  </label>

                  <div className="kiosk-field-row">
                    <label className="kiosk-field">
                      <span className="kiosk-label">Setor</span>
                      <select
                        value={setorId}
                        onChange={(e) => {
                          setSetorId(e.target.value);
                          setMaquinaId("");
      setStep(1);
                          setDefeitoId("");
                          setDefeitoCustom("");
                        }}
                        disabled={carregandoSetores}
                        data-testid="select-setor"
                      >
                        <option value="">
                          {carregandoSetores ? "Carregando..." : "Selecione o setor"}
                        </option>
                        {setoresFiltrados.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.nome || "(sem nome)"}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="kiosk-field">
                      <span className="kiosk-label">Máquina</span>
                      <select
                        value={maquinaId}
                        onChange={(e) => {
                          setMaquinaId(e.target.value);
      setStep(1);
                          setDefeitoId("");
                          setDefeitoCustom("");
                        }}
                        disabled={carregandoMaquinas || !setorId}
                        data-testid="select-maquina"
                      >
                        <option value="">
                          {carregandoMaquinas ? "Carregando..." : setorId ? "Selecione" : "Selecione o setor"}
                        </option>
                        {maquinasFiltradas.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.nome || "(sem nome)"}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                </div>

                                    </>
                  ) : (
                    <>
<div className="kiosk-form-section">
                  <h3 className="kiosk-section-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Problema
                  </h3>
                  <label className="kiosk-field">
                    <span className="kiosk-label">Tipo de Defeito</span>
                    <select
                      value={defeitoId}
                      onChange={(e) => setDefeitoId(e.target.value)}
                      disabled={carregandoDefeitos || !maquinaId}
                      data-testid="select-defeito"
                    >
                      <option value="">
                        {carregandoDefeitos ? "Carregando..." : maquinaId ? "Selecione o defeito" : "Selecione a máquina"}
                      </option>
                      {defeitos.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.nome || "(sem nome)"}
                        </option>
                      ))}
                      {maquinaId && <option value="__OUTROS__">Outro defeito...</option>}
                    </select>
                  </label>

                  {defeitoId === "__OUTROS__" && (
                    <label className="kiosk-field">
                      <span className="kiosk-label">Descreva o defeito</span>
                      <input
                        type="text"
                        value={defeitoCustom}
                        onChange={(e) => setDefeitoCustom(e.target.value)}
                        placeholder="Ex: Motor não liga, vazamento..."
                        data-testid="input-defeito-custom"
                      />
                    </label>
                  )}

                  <label className="kiosk-field">
                    <span className="kiosk-label">Observações <span className="kiosk-optional">(opcional)</span></span>
                    <textarea
                      value={descricao}
                      onChange={(e) => setDescricao(e.target.value)}
                      placeholder="Detalhes adicionais sobre o problema..."
                      rows={2}
                      data-testid="textarea-descricao"
                    />
                  </label>

                  <div className="kiosk-field-row kiosk-field-row--tight2">
                    <label className="kiosk-field">
                      <span className="kiosk-label">Criticidade</span>
                      <select 
                        value={criticidade} 
                        onChange={(e) => setCriticidade(e.target.value)}
                        data-testid="select-criticidade"
                      >
                        <option value="Baixa">Baixa</option>
                        <option value="Média">Média</option>
                        <option value="Alta">Alta</option>
                        <option value="Urgente">Urgente</option>
                      </select>
                    </label>

                    <label className="kiosk-field">
                      <span className="kiosk-label">Máquina parou?</span>
                      <select 
                        value={maquinaParou} 
                        onChange={(e) => setMaquinaParou(e.target.value)}
                        data-testid="select-maquina-parou"
                      >
                        <option value="Sim">Sim, parou</option>
                        <option value="Não">Não, funcionando</option>
                      </select>
                    </label>
                  </div>
                </div>

                                    </>
                  )}
                </div>

                <div className="kiosk-form-actions">
                  {step === 1 ? (
                    <button
                      type="submit"
                      className="kiosk-next-btn"
                      disabled={carregandoSetores || carregandoMaquinas || carregandoStatus || !setorId || !maquinaId}
                      data-testid="button-next-step"
                    >
                      Continuar
                    </button>
                  ) : (
                    <div className="kiosk-actions-row">
                      <button
                        type="button"
                        className="kiosk-back-btn"
                        onClick={() => setStep(1)}
                        disabled={salvando}
                        data-testid="button-back-step"
                      >
                        Voltar
                      </button>

                      <button
                        type="submit"
                        className="kiosk-submit-btn"
                        disabled={salvando || carregandoStatus}
                        data-testid="button-submit-os"
                      >
                        {salvando ? (
                          <>
                            <span className="kiosk-spinner"></span>
                            Abrindo OS...
                          </>
                        ) : (
                          <>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                            Abrir Ordem de Serviço
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                </form>
            )}
          </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default KioskNovaOS;
